export type DecisionStatus = "draft" | "confirmed" | "ordered" | "installed";

export type TopicDef = {
  group: string; // used for optgroup
  value: string; // stored in DB (topic)
  defaultImpacts?: { plumbing?: boolean; electrical?: boolean; layout?: boolean };
};

export const DECISION_STATUSES: { value: DecisionStatus; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "confirmed", label: "Confirmed" },
  { value: "ordered", label: "Ordered" },
  { value: "installed", label: "Installed" },
];

export const TOPICS: TopicDef[] = [
  // -------------------------
  // Bathroom
  // -------------------------
  { group: "Bathroom – Fixtures", value: "Vanity", defaultImpacts: { plumbing: true } },
  { group: "Bathroom – Fixtures", value: "Vanity size", defaultImpacts: { layout: true } },
  { group: "Bathroom – Fixtures", value: "Vanity type (floating / freestanding)", defaultImpacts: { plumbing: true } },
  { group: "Bathroom – Fixtures", value: "Sink type (undermount / vessel / integrated)", defaultImpacts: { plumbing: true } },
  { group: "Bathroom – Fixtures", value: "Faucet", defaultImpacts: { plumbing: true } },
  { group: "Bathroom – Fixtures", value: "Toilet", defaultImpacts: { plumbing: true } },
  { group: "Bathroom – Fixtures", value: "Bidet / bidet seat", defaultImpacts: { plumbing: true, electrical: true } },

  { group: "Bathroom – Shower / Tub", value: "Shower valve / trim", defaultImpacts: { plumbing: true } },
  { group: "Bathroom – Shower / Tub", value: "Shower head", defaultImpacts: { plumbing: true } },
  { group: "Bathroom – Shower / Tub", value: "Hand shower", defaultImpacts: { plumbing: true } },
  { group: "Bathroom – Shower / Tub", value: "Tub / tub filler", defaultImpacts: { plumbing: true } },
  { group: "Bathroom – Shower / Tub", value: "Shower drain type (linear / center)", defaultImpacts: { plumbing: true, layout: true } },
  { group: "Bathroom – Shower / Tub", value: "Niche / shelves", defaultImpacts: { layout: true } },

  { group: "Bathroom – Tile", value: "Floor tile", defaultImpacts: { layout: true } },
  { group: "Bathroom – Tile", value: "Floor tile size", defaultImpacts: { layout: true } },
  { group: "Bathroom – Tile", value: "Floor tile layout", defaultImpacts: { layout: true } },
  { group: "Bathroom – Tile", value: "Shower wall tile", defaultImpacts: { layout: true } },
  { group: "Bathroom – Tile", value: "Shower tile size", defaultImpacts: { layout: true } },
  { group: "Bathroom – Tile", value: "Shower tile layout", defaultImpacts: { layout: true } },
  { group: "Bathroom – Tile", value: "Accent tile / feature wall", defaultImpacts: { layout: true } },
  { group: "Bathroom – Tile", value: "Grout color", defaultImpacts: {} },

  { group: "Bathroom – Glass / Enclosure", value: "Shower glass type (frameless / framed)", defaultImpacts: { layout: true } },
  { group: "Bathroom – Glass / Enclosure", value: "Shower door type (hinged / sliding)", defaultImpacts: { layout: true } },
  { group: "Bathroom – Glass / Enclosure", value: "Hardware finish", defaultImpacts: {} },

  { group: "Bathroom – Electrical", value: "Mirror type (LED / standard)", defaultImpacts: { electrical: true } },
  { group: "Bathroom – Electrical", value: "Sconces over mirror", defaultImpacts: { electrical: true } },
  { group: "Bathroom – Electrical", value: "Ceiling lights", defaultImpacts: { electrical: true } },
  { group: "Bathroom – Electrical", value: "Shower light", defaultImpacts: { electrical: true } },
  { group: "Bathroom – Electrical", value: "Exhaust fan", defaultImpacts: { electrical: true } },
  { group: "Bathroom – Electrical", value: "Heated floor / thermostat", defaultImpacts: { electrical: true } },
  { group: "Bathroom – Electrical", value: "Outlet location (GFCI)", defaultImpacts: { electrical: true, layout: true } },
  { group: "Bathroom – Electrical", value: "Switch locations", defaultImpacts: { electrical: true, layout: true } },

  { group: "Bathroom – Accessories", value: "Towel bar", defaultImpacts: { layout: true } },
  { group: "Bathroom – Accessories", value: "Towel hook", defaultImpacts: { layout: true } },
  { group: "Bathroom – Accessories", value: "Robe hook", defaultImpacts: { layout: true } },
  { group: "Bathroom – Accessories", value: "Toilet paper holder", defaultImpacts: { layout: true } },
  { group: "Bathroom – Accessories", value: "Grab bars", defaultImpacts: { layout: true } },

  { group: "Bathroom – Paint", value: "Wall paint color", defaultImpacts: {} },
  { group: "Bathroom – Paint", value: "Wall paint sheen", defaultImpacts: {} },
  { group: "Bathroom – Paint", value: "Ceiling paint color", defaultImpacts: {} },
  { group: "Bathroom – Paint", value: "Ceiling paint sheen", defaultImpacts: {} },
  { group: "Bathroom – Paint", value: "Trim paint color", defaultImpacts: {} },
  { group: "Bathroom – Paint", value: "Trim paint sheen", defaultImpacts: {} },

  // -------------------------
  // Kitchen
  // -------------------------
  { group: "Kitchen – Cabinets", value: "Cabinet style", defaultImpacts: { layout: true } },
  { group: "Kitchen – Cabinets", value: "Cabinet color", defaultImpacts: {} },
  { group: "Kitchen – Cabinets", value: "Cabinet finish", defaultImpacts: {} },
  { group: "Kitchen – Cabinets", value: "Cabinet hardware", defaultImpacts: {} },
  { group: "Kitchen – Cabinets", value: "Pantry cabinet", defaultImpacts: { layout: true } },
  { group: "Kitchen – Cabinets", value: "Island cabinets", defaultImpacts: { layout: true } },

  { group: "Kitchen – Countertops / Backsplash", value: "Countertop material", defaultImpacts: { layout: true } },
  { group: "Kitchen – Countertops / Backsplash", value: "Countertop color", defaultImpacts: {} },
  { group: "Kitchen – Countertops / Backsplash", value: "Countertop edge profile", defaultImpacts: {} },
  { group: "Kitchen – Countertops / Backsplash", value: "Backsplash", defaultImpacts: { layout: true } },
  { group: "Kitchen – Countertops / Backsplash", value: "Backsplash grout color", defaultImpacts: {} },

  { group: "Kitchen – Appliances", value: "Refrigerator", defaultImpacts: { layout: true } },
  { group: "Kitchen – Appliances", value: "Range / cooktop", defaultImpacts: { electrical: true, layout: true } },
  { group: "Kitchen – Appliances", value: "Wall oven", defaultImpacts: { electrical: true, layout: true } },
  { group: "Kitchen – Appliances", value: "Microwave", defaultImpacts: { electrical: true, layout: true } },
  { group: "Kitchen – Appliances", value: "Dishwasher", defaultImpacts: { plumbing: true, electrical: true, layout: true } },
  { group: "Kitchen – Appliances", value: "Vent hood", defaultImpacts: { electrical: true, layout: true } },

  { group: "Kitchen – Plumbing", value: "Kitchen sink", defaultImpacts: { plumbing: true } },
  { group: "Kitchen – Plumbing", value: "Faucet", defaultImpacts: { plumbing: true } },
  { group: "Kitchen – Plumbing", value: "Garbage disposal", defaultImpacts: { plumbing: true, electrical: true } },
  { group: "Kitchen – Plumbing", value: "Water filter", defaultImpacts: { plumbing: true } },
  { group: "Kitchen – Plumbing", value: "Pot filler", defaultImpacts: { plumbing: true } },

  { group: "Kitchen – Electrical", value: "Pendant lights", defaultImpacts: { electrical: true } },
  { group: "Kitchen – Electrical", value: "Recessed lights", defaultImpacts: { electrical: true } },
  { group: "Kitchen – Electrical", value: "Under cabinet lighting", defaultImpacts: { electrical: true } },
  { group: "Kitchen – Electrical", value: "Island outlets", defaultImpacts: { electrical: true, layout: true } },
  { group: "Kitchen – Electrical", value: "Switch locations", defaultImpacts: { electrical: true, layout: true } },

  { group: "Kitchen – Flooring", value: "Floor material", defaultImpacts: { layout: true } },
  { group: "Kitchen – Flooring", value: "Floor layout", defaultImpacts: { layout: true } },
  { group: "Kitchen – Flooring", value: "Transitions", defaultImpacts: { layout: true } },

  // -------------------------
  // Whole House (for paint-only jobs)
  // -------------------------
  { group: "Whole House – Paint", value: "Wall color", defaultImpacts: {} },
  { group: "Whole House – Paint", value: "Wall sheen", defaultImpacts: {} },
  { group: "Whole House – Paint", value: "Ceiling color", defaultImpacts: {} },
  { group: "Whole House – Paint", value: "Ceiling sheen", defaultImpacts: {} },
  { group: "Whole House – Paint", value: "Trim color", defaultImpacts: {} },
  { group: "Whole House – Paint", value: "Trim sheen", defaultImpacts: {} },
  { group: "Whole House – Paint", value: "Door color", defaultImpacts: {} },

  // -------------------------
  // Whole House – Flooring
  // -------------------------
  { group: "Whole House – Flooring", value: "Flooring type (hardwood / LVP / tile / carpet)", defaultImpacts: { layout: true } },
  { group: "Whole House – Flooring", value: "Floor material / brand", defaultImpacts: {} },
  { group: "Whole House – Flooring", value: "Floor color / finish", defaultImpacts: {} },
  { group: "Whole House – Flooring", value: "Plank / tile size", defaultImpacts: { layout: true } },
  { group: "Whole House – Flooring", value: "Layout / pattern (straight / diagonal / herringbone)", defaultImpacts: { layout: true } },
  { group: "Whole House – Flooring", value: "Transition pieces", defaultImpacts: { layout: true } },
  { group: "Whole House – Flooring", value: "Baseboard / shoe molding", defaultImpacts: { layout: true } },
  { group: "Whole House – Flooring", value: "Stair treads / risers", defaultImpacts: { layout: true } },
  { group: "Whole House – Flooring", value: "Underlayment type", defaultImpacts: {} },

  // Catch-all
  { group: "Other", value: "Other", defaultImpacts: {} },
];

export function topicGroups(): string[] {
  const set = new Set<string>();
  for (const t of TOPICS) set.add(t.group);
  return Array.from(set);
}

export function findTopicDef(topic: string): TopicDef | undefined {
  const q = (topic || "").trim().toLowerCase();
  return TOPICS.find((t) => t.value.toLowerCase() === q);
}

/**
 * Topic Packs
 * Purpose: let user add multiple decision points in one click
 * without changing the DB schema (still stores single topic per decision row).
 */
export type TopicPack = {
  key: string;   // stable id
  label: string; // UI label
  groupPrefix?: string; // if provided, auto builds from TOPICS groups that start with this prefix + " – "
  groups?: string[]; // or explicit group list
};

function topicsForGroup(groupName: string): string[] {
  return TOPICS.filter((t) => t.group === groupName).map((t) => t.value);
}

function topicsForGroupPrefix(prefix: string): string[] {
  const p = (prefix || "").trim();
  if (!p) return [];
  const matchPrefix = `${p} – `;
  const groups = Array.from(new Set(TOPICS.map((t) => t.group))).filter((g) => g.startsWith(matchPrefix));
  const out: string[] = [];
  for (const g of groups) out.push(...topicsForGroup(g));
  return out;
}

export const TOPIC_PACKS: TopicPack[] = [
  // Bathroom
  { key: "bath_all", label: "Bathroom — All", groupPrefix: "Bathroom" },
  { key: "bath_fixtures", label: "Bathroom — Fixtures", groups: ["Bathroom – Fixtures"] },
  { key: "bath_shower", label: "Bathroom — Shower / Tub", groups: ["Bathroom – Shower / Tub"] },
  { key: "bath_tile", label: "Bathroom — Tile", groups: ["Bathroom – Tile"] },
  { key: "bath_glass", label: "Bathroom — Glass / Enclosure", groups: ["Bathroom – Glass / Enclosure"] },
  { key: "bath_electrical", label: "Bathroom — Electrical", groups: ["Bathroom – Electrical"] },
  { key: "bath_accessories", label: "Bathroom — Accessories", groups: ["Bathroom – Accessories"] },
  { key: "bath_paint", label: "Bathroom — Paint", groups: ["Bathroom – Paint"] },

  // Kitchen
  { key: "kitchen_all", label: "Kitchen — All", groupPrefix: "Kitchen" },
  { key: "kitchen_cabinets", label: "Kitchen — Cabinets", groups: ["Kitchen – Cabinets"] },
  { key: "kitchen_ct_bs", label: "Kitchen — Countertops / Backsplash", groups: ["Kitchen – Countertops / Backsplash"] },
  { key: "kitchen_appliances", label: "Kitchen — Appliances", groups: ["Kitchen – Appliances"] },
  { key: "kitchen_plumbing", label: "Kitchen — Plumbing", groups: ["Kitchen – Plumbing"] },
  { key: "kitchen_electrical", label: "Kitchen — Electrical", groups: ["Kitchen – Electrical"] },
  { key: "kitchen_flooring", label: "Kitchen — Flooring", groups: ["Kitchen – Flooring"] },

  // Whole house
  { key: "whole_paint", label: "Whole House — Paint", groups: ["Whole House – Paint"] },
  { key: "whole_flooring", label: "Whole House — Flooring", groups: ["Whole House – Flooring"] },
];

export function packTopics(packKey: string): string[] {
  const p = (packKey || "").trim();
  if (!p) return [];
  const pack = TOPIC_PACKS.find((x) => x.key === p);
  if (!pack) return [];

  if (pack.groups?.length) {
    const out: string[] = [];
    for (const g of pack.groups) out.push(...topicsForGroup(g));
    return out;
  }

  if (pack.groupPrefix) {
    return topicsForGroupPrefix(pack.groupPrefix);
  }

  return [];
}